/**
 * Widgets-Reiter unter Einstellungen (W1.6/W2.1, KONZEPT-WIDGETS § 7 "Editor-Erlebnis") —
 * NUR NOCH Liste + "Widget anlegen" + Lösch-Modal (Umbau 18.08.2026, HANDOFF-2026-08-18-UX-WIDGET-
 * EDITOR.md): "Widget anlegen" legt sofort einen Formular-Draft an (`POST /api/widgets`) und
 * leitet auf die neue Editor-Route `/einstellungen/widgets/:id` weiter; ein Klick auf eine Zeile
 * führt ebendorthin. Bearbeiten, Einbinden, Publizieren/Pausieren wohnen jetzt ausschließlich im
 * Editor (apps/web/src/pages/widget-editor/*) — der alte In-Place-Drawer und das Einbinden-Modal
 * sind komplett entfallen.
 *
 * W2.1 — Feld-Modell (dreischichtig: Pflicht-/Standard-/freie Felder, KONZEPT § 7). Diese Datei
 * bleibt trotz der Reduktion der EINZIGE Ort, an dem `EDITOR_PFLICHTFELDER`/`EDITOR_STANDARDFELDER`/
 * `FREIE_TYP_LABEL` als literale `const …`-Blöcke stehen: `apps/api/test/widget-formular.test.ts`
 * scannt GENAU DIESE Datei als Text (Marker-Suche, kein Import) und hält sie deckungsgleich mit
 * dem Server-Modell. Der Widget-Editor importiert Modell/Funktionen von hier, statt sie zu
 * duplizieren — siehe Kopf-Kommentar an `Widget` weiter unten.
 *
 * W3.4 — Bewertungs-Widgets: die Anlage-Sperre ohne bestätigten Google-Eintrag griff bisher im
 * Anlage-Drawer; dieser ist entfallen, und "Widget anlegen" legt jetzt ausschließlich kind='form'
 * an (matcht den E2E-Golden-Path). Die Anlage eines Bewertungs-Widgets ist über diese Oberfläche
 * aktuell NICHT erreichbar — echte, im Bauplan nicht abgedeckte Funktionslücke, siehe Rückmeldung
 * der Bau-Session. Bereits bestehende Bewertungs-Widgets bleiben unverändert gelistet UND tragen
 * weiterhin den Hinweis, wenn der Google-Eintrag (noch) nicht bestätigt ist.
 *
 * Beträge kommen hier nirgends vor: die Widget-Konfiguration ist Darstellung, nie Preisquelle.
 */
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useApi, apiMutate } from "../hooks/useApi";
import { useContentRefetch } from "../ui/CopilotProvider";
import { PILL_CLASSES, formatDateTime } from "../ui/format";
import { Button, Card, EmptyState, ErrorNote, Icon, LoadingRows, Modal, Select } from "../ui/components";
import { aussehenVariablen, type Aussehen } from "@maestro/widgets/aussehen";
import type { MenuCard, MenuCardType } from "../ui/types";

/** Von `widget-editor/*` mitgenutzt (Route /einstellungen/widgets/:id). WICHTIG: diese Datei bleibt
 *  der EINZIGE Ort, an dem `EDITOR_PFLICHTFELDER`/`EDITOR_STANDARDFELDER`/`FREIE_TYP_LABEL` als
 *  literale `const …`-Bloecke stehen — `apps/api/test/widget-formular.test.ts` scannt GENAU DIESE
 *  Datei als Text (Marker-Suche, kein Import) und haelt sie deckungsgleich mit dem Server-Modell.
 *  Der Editor importiert die Werte von hier, statt eine zweite Kopie zu fuehren. */
export interface Widget {
  id: string;
  kind: "form" | "reviews";
  name: string;
  allowedDomains: string[];
  draftConfig: Record<string, unknown>;
  publishedConfig: Record<string, unknown> | null;
  publishedAt: string | null;
  version: number;
  status: "draft" | "live" | "paused";
  updatedAt: string;
}

export const KIND_LABEL: Record<Widget["kind"], string> = { form: "Formular", reviews: "Bewertungen" };
export const STATUS_LABEL: Record<Widget["status"], string> = { draft: "Entwurf", live: "Live", paused: "Pausiert" };
// Design E9: Status-Farben einheitlich mit dem Editor — Live gruen, Pausiert gelb, Entwurf neutral.
export const STATUS_BADGE: Record<Widget["status"], string> = {
  draft: PILL_CLASSES.neutral,
  live: PILL_CLASSES.success,
  paused: PILL_CLASSES.warn,
};
const badge = "rounded-full px-2 py-0.5 font-label-md text-label-md";

/* ---------------------------------------------------------------------------------------------
 * W2.1 — Feld-Modell (Anzeige-Spiegel von apps/api/src/lib/widget-formular.ts)
 * ------------------------------------------------------------------------------------------- */

/** Schicht 1: fest verdrahtet, nicht abwaehlbar — nur zur Anzeige. */
export const EDITOR_PFLICHTFELDER = [
  { key: "customerName", label: "Name" },
  { key: "customerEmail", label: "E-Mail" },
  { key: "phone", label: "Telefon" },
  { key: "eventDate", label: "Wunschtermin" },
  { key: "guests", label: "Personenzahl" },
  { key: "message", label: "Ihre Nachricht" },
];

/** Schicht 2a: an-/abwaehlbare Standardfelder (Allowlist des Servers). */
export const EDITOR_STANDARDFELDER = [
  { key: "company", label: "Firma" },
  { key: "serviceKind", label: "Wo findet die Veranstaltung statt?" },
  { key: "deliveryAddress", label: "Lieferadresse" },
  { key: "budget", label: "Budgetvorstellung (Freitext)" },
  { key: "dietaryRequirements", label: "Unverträglichkeiten / Ernährung" },
  { key: "dateFlexible", label: "Termin ist flexibel" },
  { key: "arrivalTime", label: "Gewünschte Uhrzeit" },
  { key: "preferredMenu", label: "Menü-Wunsch" },
  { key: "format", label: "Format der Veranstaltung" },
];

/** Schicht 2b: wird immer erfasst, ist nicht wählbar — steht nur in der Schlüssel-Sperrliste. */
export const EDITOR_AUTOMATISCH = ["originalPage", "referrer", "utmSource", "utmMedium", "utmCampaign", "utmTerm", "utmContent"];

/**
 * Auswahlliste der Feldtypen — WORTGLEICH (in Schluesseln und Reihenfolge) mit `FREIE_FELDTYPEN`
 * in apps/api/src/lib/widget-formular.ts und `FREIE_TYPEN` in apps/widgets/src/felder.ts. Eine
 * Ratsche in apps/api/test/widget-formular.test.ts haelt die drei Listen deckungsgleich: ein Typ,
 * den nur der Server kennt, waere hier unerreichbar; einer, den nur diese Liste kennt, brächte
 * dem Betreiber beim Speichern ein 422.
 *
 * KEIN Zahlentyp — das einzige Zahlenfeld des Modells ist die fixe Personenzahl (Preis-Integritaet).
 */
export const FREIE_TYP_LABEL: Record<string, string> = {
  text: "Text (einzeilig)",
  textarea: "Text (mehrzeilig)",
  select: "Auswahl",
  checkbox: "Ja/Nein",
  date: "Datum",
  email: "E-Mail",
  tel: "Telefon",
  time: "Uhrzeit",
  file: "Datei-Upload",
};
export const MAX_FREIE_FELDER = 10;

export const BELEGTE_SCHLUESSEL = new Set([
  ...EDITOR_PFLICHTFELDER.map((f) => f.key),
  ...EDITOR_STANDARDFELDER.map((f) => f.key),
  ...EDITOR_AUTOMATISCH,
]);

export interface FreiesFeld {
  key: string;
  typ: string;
  label: string;
  pflicht: boolean;
  optionen?: string[];
}

/** Editor-Zeile: wie FreiesFeld, plus die Frage, ob der Schluessel schon eingefroren ist. */
export interface FreieZeile extends FreiesFeld {
  /** Gespeicherte Felder behalten ihren Schluessel; neue folgen der Beschriftung, bis gespeichert
   *  wird. So bleibt der Schluessel im `details`-JSON stabil, auch wenn der Betreiber umbenennt. */
  keyFest: boolean;
  optionenText: string;
}

/** Beschriftung → slug-artiger, kollisionsfreier Schluessel (Server-Muster: /^[a-z][a-z0-9_]{0,39}$/). */
export function schluesselAus(label: string, belegt: Set<string>): string {
  const basis = label
    .toLowerCase()
    .replace(/ä/g, "ae")
    .replace(/ö/g, "oe")
    .replace(/ü/g, "ue")
    .replace(/ß/g, "ss")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 40);
  if (!basis) return "";
  let key = /^[a-z]/.test(basis) ? basis : `feld_${basis}`.slice(0, 40);
  let n = 2;
  while (belegt.has(key)) key = `${key.slice(0, 37)}_${n++}`;
  return key;
}

export function parseDomains(text: string): string[] {
  return text
    .split("\n")
    .map((z) => z.trim())
    .filter(Boolean);
}

/** `draftConfig.formular` in Editor-Zeilen uebersetzen (defensiv — fremdes JSON). */
export function leseFormular(config: Record<string, unknown>): { standardfelder: string[]; freieFelder: FreieZeile[] } {
  const roh = config.formular;
  if (typeof roh !== "object" || roh === null || Array.isArray(roh)) return { standardfelder: [], freieFelder: [] };
  const f = roh as Record<string, unknown>;
  const standardfelder = Array.isArray(f.standardfelder) ? f.standardfelder.filter((k): k is string => typeof k === "string") : [];
  const freieFelder = (Array.isArray(f.freieFelder) ? f.freieFelder : [])
    .filter((e): e is Record<string, unknown> => typeof e === "object" && e !== null)
    .map((e) => {
      const optionen = Array.isArray(e.optionen) ? e.optionen.filter((o): o is string => typeof o === "string") : undefined;
      return {
        key: typeof e.key === "string" ? e.key : "",
        typ: typeof e.typ === "string" ? e.typ : "text",
        label: typeof e.label === "string" ? e.label : "",
        pflicht: e.pflicht === true,
        keyFest: true,
        optionenText: (optionen ?? []).join("\n"),
        ...(optionen ? { optionen } : {}),
      } satisfies FreieZeile;
    });
  return { standardfelder, freieFelder };
}

/** Editor-Zeilen zurueck in die gespeicherte Form (der Server validiert sie erneut). */
export function baueFormular(standardfelder: string[], zeilen: FreieZeile[]): Record<string, unknown> {
  return {
    standardfelder,
    freieFelder: zeilen.map((z) => ({
      key: z.key,
      typ: z.typ,
      label: z.label.trim(),
      pflicht: z.pflicht,
      ...(z.typ === "select"
        ? { optionen: z.optionenText.split("\n").map((o) => o.trim()).filter(Boolean) }
        : {}),
    })),
  };
}

/** Zwei-Zeilen-Snippet gemaess E-W9: Loader vom API-Worker des Mandanten + Platzhalter-DIV.
 * window.location.origin IST in Produktion die Mandanten-Domain (die Web-App laeuft auf der
 * Tenant-Subdomain, gleicher Origin wie der API-Worker); im Dev zeigt es localhost — dort ist
 * das Snippet ohnehin nur zur Ansicht.
 *
 * Reisegruppen-Widget T5: mit `anker` traegt der Platzhalter `id="gruppe-anfragen"` (Sprungziel fuer
 * „Gruppe anfragen“ auf der Seite) und mit `sprache` ein `lang`-Attribut (Screenreader, Silbentrennung).
 * Ohne Optionen bleibt die Ausgabe wie bisher exakt gleich. */
/** Punkt 134: der Stil-Block nur noch auf Wunsch (Haken in „Einbinden“), gebaut aus den gespeicherten
 * Werten aus „Aussehen“. `aussehenVariablen` laesst nur feste Werte durch (Hex, Schrift-Tabelle,
 * Ecken-Stufe), nichts davon kann den Block verlassen. */
export function stilBlock(aussehen: Aussehen): string {
  const werte = Object.entries(aussehenVariablen(aussehen)).map(([k, v]) => `${k}:${v}`);
  return `<style>[data-maestro-widget]{${werte.join(";")}}</style>`;
}

export function snippetFuer(widgetId: string, optionen: { anker?: boolean; sprache?: string; stil?: Aussehen } = {}): string {
  const anker = optionen.anker ? ` id="gruppe-anfragen"` : "";
  const sprache = optionen.sprache ? ` lang="${optionen.sprache}"` : "";
  return [
    ...(optionen.stil ? [stilBlock(optionen.stil)] : []),
    `<script src="${window.location.origin}/api/public/widgets/v1/maestro.js" defer></script>`,
    `<div${anker} data-maestro-widget="${widgetId}"${sprache}></div>`,
  ].join("\n");
}

/** Beispiel fuer eine Menuekarte auf der Website: der Wunsch geht still ans Formular mit (Plan A6, Menue-Signal). */
export function menueLinkBeispiel(schluessel: string): string {
  return `<a href="#gruppe-anfragen" data-maestro-menue="${schluessel}">Anfragen</a>`;
}

/** Punkt 150 P4: Sprachen, in denen die Website-Speisekarte ausgeliefert wird (fehlt eine
 *  Uebersetzung, faellt die API auf Deutsch zurueck). */
export const SPEISEKARTE_SPRACHEN = [
  { code: "de", label: "Deutsch" },
  { code: "en", label: "Englisch" },
  { code: "it", label: "Italienisch" },
  { code: "fr", label: "Französisch" },
] as const;

/** Einbau-Schnipsel fuer die Speisekarte: genau eine Karte (`karte` = Slug) oder alle gueltigen
 *  Karten einer Kartenart als Reiter (`kartenart` = ID). Slug und ID sind serverseitig geprueft
 *  (Slug-Regex bzw. UUID), landen hier also nie als freier Text im Attribut. */
export function speisekarteSnippet(ziel: { karte: string } | { kartenart: string }, sprache: string): string {
  const attr = "karte" in ziel ? `data-maestro-speisekarte="${ziel.karte}"` : `data-maestro-kartenart="${ziel.kartenart}"`;
  return [
    `<script src="${window.location.origin}/api/public/widgets/v1/maestro.js" defer></script>`,
    `<div ${attr} data-maestro-lang="${sprache}"></div>`,
  ].join("\n");
}

/** Punkt 150 P4: Abschnitt „Speisekarte“ — Schnipsel je veroeffentlichter Karte bzw. Kartenart
 *  und Sprache. Gerechnet und gefiltert wird nichts, das macht der Server beim Ausliefern. */
export function SpeisekarteEinbau() {
  const kartenQ = useApi<MenuCard[]>("/api/menu-cards");
  const artenQ = useApi<MenuCardType[]>("/api/menu-card-types");
  const [sprache, setSprache] = useState<string>("de");
  const [kopiert, setKopiert] = useState<string | null>(null);
  const karten = (kartenQ.data ?? []).filter((k) => k.active && k.publishedVersion != null && k.slug);
  const arten = (artenQ.data ?? []).filter((a) => !a.archivedAt && karten.some((k) => k.cardTypeId === a.id));
  const zeilen = [
    ...arten.map((a) => ({ key: `art-${a.id}`, titel: a.name, hinweis: "Alle gültigen Karten dieser Kartenart als Reiter", code: speisekarteSnippet({ kartenart: a.id }, sprache) })),
    ...karten.map((k) => ({ key: `karte-${k.id}`, titel: k.name, hinweis: `Nur diese Karte · /${k.slug}`, code: speisekarteSnippet({ karte: k.slug! }, sprache) })),
  ];
  async function kopiere(key: string, code: string) {
    try {
      await navigator.clipboard.writeText(code);
      setKopiert(key);
    } catch {
      /* ohne Zwischenablage (kein HTTPS) bleibt der Text zum Markieren stehen */
    }
  }
  return (
    <Card className="mt-6 p-4" data-testid="speisekarte-einbau">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="font-body-md text-body-md font-semibold text-on-background dark:text-inverse-on-surface">Speisekarte</p>
          <p className="font-label-md text-label-md text-secondary dark:text-secondary-fixed-dim">
            Zeigt deine veröffentlichte Speisekarte auf deiner Website — Änderungen erscheinen dort nach dem Veröffentlichen von selbst.
          </p>
        </div>
        <Select
          ariaLabel="Sprache"
          value={sprache}
          onValueChange={setSprache}
          options={SPEISEKARTE_SPRACHEN.map((s) => ({ value: s.code, label: s.label }))}
          className="!w-auto !py-1.5"
          testId="speisekarte-sprache"
        />
      </div>
      {kartenQ.loading || artenQ.loading ? (
        <LoadingRows rows={2} />
      ) : kartenQ.error ? (
        <ErrorNote message={kartenQ.error} />
      ) : zeilen.length === 0 ? (
        <EmptyState icon="restaurant_menu" title="Noch keine Karte veröffentlicht" hint="Veröffentliche unter Speisekarten eine Karte, dann erscheint hier ihr Einbau-Code." />
      ) : (
        <ul className="space-y-3">
          {zeilen.map((z) => (
            <li key={z.key}>
              <div className="mb-1 flex items-center justify-between gap-3">
                <p className="font-label-md text-label-md text-on-background dark:text-inverse-on-surface">
                  <span className="font-semibold">{z.titel}</span> <span className="text-secondary">· {z.hinweis}</span>
                </p>
                <Button type="button" variant="secondary" onClick={() => void kopiere(z.key, z.code)}>
                  <Icon name={kopiert === z.key ? "check" : "content_copy"} className="text-[16px]" /> {kopiert === z.key ? "Kopiert" : "Kopieren"}
                </Button>
              </div>
              <pre className="overflow-x-auto rounded-lg bg-surface-container-high p-3 font-mono text-[12px]">{z.code}</pre>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

export function WidgetsTab() {
  const { data, loading, error, reload } = useApi<Widget[]>("/api/widgets");
  // F6: der Assistent legt Widgets an/aendert sie -> Liste nachladen.
  useContentRefetch("widget", reload);
  const widgets = data ?? [];
  // W3.4: derselbe Endpunkt, den der Modul-Reiter liest (R0.12) — kein zweiter Weg zu derselben
  // Wahrheit. Trägt nur noch den Hinweis an bestehenden Bewertungs-Widgets (siehe Kopf-Kommentar) —
  // die Anlage-Sperre ist mit dem Anlage-Drawer entfallen.
  const placeQ = useApi<{ googlePlaceId?: string | null } | null>("/api/settings/module");
  const placeIdBestaetigt = typeof placeQ.data?.googlePlaceId === "string" && placeQ.data.googlePlaceId.trim() !== "";

  const navigate = useNavigate();
  const [anlageBusy, setAnlageBusy] = useState(false);
  const [aktionsFehler, setAktionsFehler] = useState<string | null>(null);
  // W1.7: Loeschen mit Rueckfrage (Muster builder.tsx Loeschen-Dialog) — destruktiv, nie sofort.
  // Live Widgets bekommen keinen Loeschen-Knopf: erst Pausieren (der Server erzwingt dieselbe
  // Zwei-Stufen-Regel mit 422 widget_live, Begruendung in routes/widgets.ts).
  const [loeschId, setLoeschId] = useState<string | null>(null);
  const [loeschBusy, setLoeschBusy] = useState(false);

  /** Legt sofort einen Formular-Draft an und leitet auf die neue Editor-Route weiter (Bauplan:
   *  "erstellt Draft, leitet SOFORT auf Editor-Route weiter") — die vollstaendige Feld-Definition
   *  (und sei sie leer = nur Pflichtfelder) kommt sofort mit, sonst laesst der Server das Widget
   *  spaeter nicht publizieren (siehe baueFormular-Kommentar). */
  async function anlegen() {
    setAnlageBusy(true);
    setAktionsFehler(null);
    try {
      const row = await apiMutate<Widget>("/api/widgets", "POST", {
        kind: "form",
        name: "Neues Widget",
        draftConfig: { formular: { standardfelder: [], freieFelder: [] } },
      });
      navigate(`/einstellungen/widgets/${row.id}`);
    } catch (err) {
      setAktionsFehler(err instanceof Error ? err.message : String(err));
      setAnlageBusy(false);
    }
  }

  async function loeschen(id: string) {
    setLoeschBusy(true);
    setAktionsFehler(null);
    try {
      await apiMutate(`/api/widgets/${id}`, "DELETE");
      setLoeschId(null);
      reload();
    } catch (err) {
      setAktionsFehler(err instanceof Error ? err.message : String(err));
      setLoeschId(null);
    } finally {
      setLoeschBusy(false);
    }
  }

  const loeschWidget = widgets.find((w) => w.id === loeschId) ?? null;

  return (
    <>
      {aktionsFehler && <ErrorNote message={aktionsFehler} />}

      <div className="mb-3 flex justify-end">
        <Button type="button" disabled={anlageBusy} onClick={() => void anlegen()}>
          <Icon name="add" className="text-[18px]" /> Widget anlegen
        </Button>
      </div>

      <Card className="overflow-hidden">
        {loading ? (
          <LoadingRows rows={3} />
        ) : error ? (
          <ErrorNote message={error} />
        ) : widgets.length === 0 ? (
          <EmptyState
            icon="widgets"
            title="Noch keine Widgets"
            hint="Legen Sie oben rechts ein Widget an — ein Anfrage-Formular, das Sie per Copy-Paste-Snippet auf Ihrer eigenen Website einbinden."
          />
        ) : (
          <ul className="divide-y divide-outline-variant/20 dark:divide-outline/20">
            {widgets.map((w) => (
              <li
                key={w.id}
                className="flex cursor-pointer items-start justify-between gap-4 p-4 hover:bg-surface-container-high/60 dark:hover:bg-white/[0.03]"
                onClick={() => navigate(`/einstellungen/widgets/${w.id}`)}
              >
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-body-md text-body-md font-semibold text-on-background dark:text-inverse-on-surface">{w.name}</p>
                    <span className={`${badge} bg-primary-container text-on-primary-container`}>{KIND_LABEL[w.kind]}</span>
                    <span className={`${badge} ${STATUS_BADGE[w.status]}`}>{STATUS_LABEL[w.status]}</span>
                  </div>
                  <p className="mt-1 font-label-md text-label-md text-secondary dark:text-secondary-fixed-dim">
                    {w.publishedAt
                      ? `Version ${w.version} · publiziert ${formatDateTime(w.publishedAt)}`
                      : "Noch nie publiziert"}
                    {" · "}
                    {w.allowedDomains.length > 0
                      ? `Domains: ${w.allowedDomains.join(", ")}`
                      : "Keine Domain eingetragen — öffentlich nicht abrufbar"}
                  </p>
                  {/* W3.4 — der interne Hinweis in MAESTRO. Auf der Kundenseite bleibt das Widget
                      still (der Loader rendert nichts Sichtbares); dass es still ist, muss der
                      Betreiber trotzdem hier erfahren. */}
                  {w.kind === "reviews" && !placeIdBestaetigt && (
                    <p data-testid="bewertungen-hinweis" className="mt-1 flex items-start gap-1 font-label-md text-label-md text-error">
                      <Icon name="warning" className="text-[16px]" />
                      <span>
                        Ohne bestätigten Google-Eintrag zeigt dieses Widget auf Ihrer Website nichts an.{" "}
                        <Link to="?tab=module" className="underline" onClick={(e) => e.stopPropagation()}>
                          Google-Eintrag bestätigen
                        </Link>
                      </span>
                    </p>
                  )}
                </div>
                <div className="flex shrink-0 items-center gap-3">
                  {/* Loeschen nur fuer Entwurf/pausiert — ein LIVE Widget liefert gerade auf der
                      Website aus: erst Pausieren (im Editor), Zwei-Stufen-Regel (der Server
                      erzwingt sie zusaetzlich mit 422). */}
                  {w.status !== "live" && (
                    <button
                      type="button"
                      disabled={loeschBusy}
                      title="Löscht das Widget endgültig aus MAESTRO — das Snippet auf Ihrer Website liefert dann nichts mehr aus."
                      className="inline-flex items-center gap-1 font-label-md text-label-md text-error hover:underline disabled:opacity-50"
                      onClick={(e) => {
                        e.stopPropagation();
                        setLoeschId(w.id);
                      }}
                    >
                      <Icon name="delete" className="text-[16px]" /> Löschen
                    </button>
                  )}
                  <Icon name="chevron_right" className="text-[20px] text-secondary-fixed-dim" />
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <SpeisekarteEinbau />

      {/* Loeschen-Rueckfrage (W1.7): destruktive Aktion nie ohne Bestaetigung (Muster
          builder.tsx). Soft-Delete serverseitig — aus Betreibersicht ist es endgueltig. */}
      <Modal
        open={loeschWidget != null}
        onClose={() => !loeschBusy && setLoeschId(null)}
        width="480px"
        gefahr
        title={<>„{loeschWidget?.name}“ löschen?</>}
        footer={
          loeschWidget && (
            <>
              <Button type="button" variant="secondary" disabled={loeschBusy} onClick={() => setLoeschId(null)}>
                Abbrechen
              </Button>
              <Button type="button" variant="danger" disabled={loeschBusy} onClick={() => void loeschen(loeschWidget.id)}>
                <Icon name="delete_forever" className="text-[16px]" /> {loeschBusy ? "Löscht …" : "Endgültig löschen"}
              </Button>
            </>
          )
        }
      >
        {loeschWidget && (
          <div className="space-y-4">
            <p className="font-body-sm text-body-sm text-on-surface-variant">
              Das Widget wird endgültig aus MAESTRO entfernt und liefert öffentlich nichts mehr aus.
              {loeschWidget.publishedAt != null
                ? " Ein noch auf Ihrer Website eingebundenes Snippet bleibt dort stehen, zeigt aber nichts mehr an — entfernen Sie es dort bei Gelegenheit."
                : ""}
              {" "}Das lässt sich nicht rückgängig machen.
            </p>
          </div>
        )}
      </Modal>
    </>
  );
}
